import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppContext } from '@layouts/LayoutAdmin/LayoutAdmin';
import { appToast } from '@utils/appToast';
import {
  ALL_STORE_VALUE,
  INVENTORY_REPORT_CONSTANT,
  isAdminLikeRole,
  TPeriodOption,
  TReportPreview,
  TReportRow,
  TStoreOption,
} from '../constant';
import useInventoryReportApi from './useInventoryReportApi';
import useInventoryReportRouter from './useInventoryReportRouter';

const { MESSAGE } = INVENTORY_REPORT_CONSTANT;

type TValidationErrors = {
  period?: string;
  store?: string;
};

function useInventoryReport() {
  const {
    role,
    companyCode: ctxCompanyCode,
    storeCode: ctxStoreCode,
    storeName: ctxStoreName,
  } = useContext(AppContext);
  const { handler: apiHandler } = useInventoryReportApi();
  const { handler: routerHandler } = useInventoryReportRouter();

  const isAdmin = isAdminLikeRole(role);

  const [periodOptions, setPeriodOptions] = useState<TPeriodOption[]>([]);
  const [storeOptions, setStoreOptions] = useState<TStoreOption[]>([]);

  // [4] 棚卸年月 — default = kỳ lớn nhất (inventoryYearMonth lớn nhất, sort desc từ API).
  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(
    undefined
  );
  // [5] 店舗 — 管理者/地区長 default "全店" (地区長 bị BE giới hạn trong sector); 各店舗 pre-fill store của user, không đổi được.
  const [selectedStore, setSelectedStore] = useState<string | undefined>(
    isAdmin ? ALL_STORE_VALUE : ctxStoreCode ?? undefined
  );

  const [preview, setPreview] = useState<TReportPreview | null>(null);
  const [allRows, setAllRows] = useState<TReportRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // refs for synchronous guards — prevent duplicate calls from stale closures
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  // ref to track current page without causing loadPreview re-run
  const currentPageRef = useRef(1);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch danh sách kỳ 棚卸年月 từ API thật (cùng endpoint /api/v1/inventory/sessions với PC004).
  // Sort desc → [0] là kỳ lớn nhất (mới nhất về mặt số), dùng làm default.
  useEffect(() => {
    apiHandler
      .getSessions()
      .then((periods) => {
        setPeriodOptions(periods);
        if (periods.length > 0) {
          setSelectedPeriod(periods[0].inventoryYearMonth);
        }
      })
      .catch(() => {
        // fallback: dropdown rỗng
      });
  }, []);

  // Fetch danh sách store từ API thật khi role=ADMIN/AREA_OWNER (BE tự lọc theo sector cho AREA_OWNER);
  // storeOwner dùng ctx, không cần list. Composite value = "${storeCode}_${companyCode}" để unique hoá giữa các company.
  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    apiHandler
      .getStores()
      .then((stores) => {
        const options: TStoreOption[] = [
          { value: ALL_STORE_VALUE, label: '全店' },
          ...stores.map((s) => ({
            value: `${s.storeCode}_${s.companyCode}`,
            label: `${s.storeCode}_${s.storeName}`,
            storeCode: s.storeCode,
            companyCode: s.companyCode,
          })),
        ];
        setStoreOptions(options);
      })
      .catch(() => {
        // fallback: dropdown rỗng, user vẫn có thể tải lại trang
      });
  }, [isAdmin]);

  const [validationErrors, setValidationErrors] = useState<TValidationErrors>(
    {}
  );
  const [showOutputDialog, setShowOutputDialog] = useState(false);
  const [loadingOutput, setLoadingOutput] = useState(false);

  const currentPeriodOption = useMemo(
    () => periodOptions.find((p) => p.inventoryYearMonth === selectedPeriod),
    [periodOptions, selectedPeriod]
  );
  const currentStoreOption = useMemo(
    () => storeOptions.find((s) => s.value === selectedStore),
    [storeOptions, selectedStore]
  );

  // Nhãn hiển thị store — dùng cho bảng tóm tắt D3 và preview [6.1]/[6.2].
  const storeDisplayLabel = isAdmin
    ? currentStoreOption?.label ?? ''
    : `${ctxStoreCode ?? ''} ${ctxStoreName ?? ''}`.trim();

  // Actual storeCode để truyền vào API — tách ra khỏi composite value cho admin.
  // storeOwner: selectedStore = ctxStoreCode (raw), không cần tách.
  const selectedStoreCode = useMemo(() => {
    if (!selectedStore) {
      return undefined;
    }
    if (selectedStore === ALL_STORE_VALUE) {
      return ALL_STORE_VALUE;
    }
    return isAdmin
      ? currentStoreOption?.storeCode ?? selectedStore
      : selectedStore;
  }, [isAdmin, selectedStore, currentStoreOption]);

  // Company code kèm theo store đã chọn — bắt buộc để BE phân biệt storeCode trùng nhau giữa các
  // company khác nhau. 管理者: lấy từ store option đã chọn (composite dropdown); 各店舗: dùng company
  // của chính user (ctxCompanyCode), vì store readonly luôn là store của user đó.
  const selectedCompanyCode = useMemo(() => {
    if (!selectedStore || selectedStore === ALL_STORE_VALUE) {
      return undefined;
    }
    return isAdmin
      ? currentStoreOption?.companyCode
      : ctxCompanyCode ?? undefined;
  }, [isAdmin, selectedStore, currentStoreOption, ctxCompanyCode]);

  // [8] PDF出力 chỉ enabled khi cả [4] và [5] có giá trị hợp lệ.
  const isOutputEnabled = Boolean(selectedPeriod) && Boolean(selectedStore);

  // [7] Reload preview mỗi khi [4] hoặc [5] đổi giá trị — gọi API thật GET .../preview
  // (api-integration.md §2.1, MSG mapping §4).
  const loadPreview = useCallback(async () => {
    if (!currentPeriodOption || !selectedStoreCode) {
      setPreview(null);
      setAllRows([]);
      setHasMore(false);
      currentPageRef.current = 1;
      setLoadingPage(false);
      return;
    }
    currentPageRef.current = 1;
    setAllRows([]);
    setHasMore(false);
    setLoadingPreview(true);
    try {
      const result = await apiHandler.getPreview(
        currentPeriodOption.inventoryYearMonth,
        selectedStoreCode,
        selectedCompanyCode,
        1
      );
      setPreview(result);
      setAllRows(result.rows);
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
    } catch (error) {
      // api-integration.md §4 + BA 2026-07-22 (F2/TC-048): nhánh PREVIEW map lỗi như sau —
      // FORBIDDEN_STORE_SCOPE → E03 (Gap — chưa có mã MSG riêng); lỗi hệ thống/unhandled
      // (500 không kèm mã nghiệp vụ error.data.errors[0].code) → E03 (TC-048: "unhandled exception,
      // không phải lỗi nghiệp vụ cụ thể"); business error có mã cụ thể khác (INVENTORY_SESSION_NOT_FOUND
      // / STORE_NOT_FOUND ...) → E01.
      const errCode = (error as { data?: { errors?: { code?: string }[] } })
        ?.data?.errors?.[0]?.code;
      const isSystemError = !errCode;
      const message =
        errCode === 'FORBIDDEN_STORE_SCOPE' || isSystemError
          ? MESSAGE.E03
          : MESSAGE.E01;
      appToast.error({ message });
      setPreview(null);
      setAllRows([]);
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      setLoadingPreview(false);
      setLoadingPage(false);
    }
    // NOTE: `apiHandler` (từ useInventoryReportApi()) cố ý không nằm trong deps — hook đó trả về object
    // mới mỗi lần render, đưa vào đây sẽ gây loop gọi lại preview vô hạn. Chỉ phụ thuộc dữ liệu thật sự
    // làm thay đổi kết quả preview: kỳ [4] + store [5] đã chọn (qua selectedStoreCode/selectedCompanyCode).
  }, [currentPeriodOption, selectedStoreCode, selectedCompanyCode]);

  const loadMore = useCallback(async () => {
    if (!currentPeriodOption || !selectedStoreCode) {
      return;
    }
    // use refs for synchronous guard — state values are stale inside rapid scroll events
    if (!hasMoreRef.current || loadingMoreRef.current) {
      return;
    }
    const nextPage = currentPageRef.current + 1;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const result = await apiHandler.getPreview(
        currentPeriodOption.inventoryYearMonth,
        selectedStoreCode,
        selectedCompanyCode,
        nextPage
      );
      currentPageRef.current = nextPage;
      setAllRows((prev) => [...prev, ...result.rows]);
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
    } catch {
      // silent — user can scroll again to retry
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [currentPeriodOption, selectedStoreCode, selectedCompanyCode]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleChangePeriod = (value: string) => {
    setSelectedPeriod(value);
    setValidationErrors((prev) => ({ ...prev, period: undefined }));
  };

  const handleChangeStore = (value: string) => {
    setSelectedStore(value);
    setValidationErrors((prev) => ({ ...prev, store: undefined }));
  };

  // [8] Validate [4][5] client-side trước khi mở dialog VAMS_PC_007_01.
  const handleClickOutput = () => {
    const errors: TValidationErrors = {};
    if (!selectedPeriod) {
      errors.period = MESSAGE.V01; // MSG-PC007-V01
    }
    if (isAdmin && !selectedStore) {
      errors.store = MESSAGE.V02; // MSG-PC007-V02 (chỉ 管理者)
    }
    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      setShowOutputDialog(true);
    }
  };

  // [D1.1]/[D4] đóng dialog, không tạo PDF, giữ nguyên [4][5].
  const handleCancelOutputDialog = () => {
    if (loadingOutput) {
      return;
    }
    setShowOutputDialog(false);
  };

  // [D5] OK — gọi API PDF thật (POST .../pdf, responseType:'blob' — api-integration.md §2.2),
  // thành công (S01): vừa mở tab mới vừa tải file PDF về máy (UTC-TC057/TC058), rồi đóng dialog + toast.
  const handleConfirmOutput = async () => {
    if (!currentPeriodOption || !selectedStoreCode) {
      return;
    }
    if (loadingOutput) {
      return; // chống double-submit khi double-click OK lúc đang loading (TC-063)
    }
    setLoadingOutput(true);
    try {
      const blob = await apiHandler.generatePdf(
        currentPeriodOption.inventoryYearMonth,
        selectedStoreCode,
        selectedCompanyCode
      );
      const url = URL.createObjectURL(blob);

      // (1) Mở PDF ở tab mới.
      window.open(url, '_blank');

      // (2) Đồng thời tải file PDF về máy.
      const fileName = `棚卸報告書_${currentPeriodOption.inventoryYearMonth}_${selectedStoreCode}.pdf`;
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Giải phóng object URL sau khi tab mới + download đã kịp xử lý.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      setShowOutputDialog(false);
      appToast.success({ message: MESSAGE.S01, duration: 2 });
    } catch (error) {
      // api-integration.md §4 + BA xác nhận (verification.md round 1, F2/TC-047/TC-061): MỌI lỗi ở
      // giai đoạn tạo PDF (business error có mã cụ thể lẫn lỗi hệ thống/network chung) đều hiển thị
      // E02 — ở lại VAMS_PC_007, giữ dialog mở (Open Item #4 analyzing-fe.md). MESSAGE.E03 không
      // còn được dùng ở nhánh này (giữ định nghĩa trong constant.ts cho các trường hợp khác).
      appToast.error({ message: MESSAGE.E02 });
    } finally {
      setLoadingOutput(false);
    }
  };

  return {
    state: {
      isAdmin,
      periodOptions,
      storeOptions,
      selectedPeriod,
      selectedStore,
      currentPeriodOption,
      storeDisplayLabel,
      preview,
      allRows,
      hasMore,
      loadingMore,
      loadingPage,
      loadingPreview,
      validationErrors,
      showOutputDialog,
      loadingOutput,
      isOutputEnabled,
      ctxStoreCode,
      ctxStoreName,
    },
    handler: {
      handleBack: routerHandler.handleBack,
      handleChangePeriod,
      handleChangeStore,
      handleClickOutput,
      handleCancelOutputDialog,
      handleConfirmOutput,
      loadMore,
    },
  };
}

export default useInventoryReport;
